import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { paginateArray } from "../../utils";
import {Employee, EmploymentStatus} from "../interfaces";
import {EOR_SALARY_CHANGE_STATUS_REQUESTED} from "../../utils/constants";

// Select all employees from the state
export const selectAllEmployees = (state: RootState) => state.employees.employees;

// Get the fetch state of all employees
export const allEmployeesFetchState = (state: RootState) => state.employees.status;

// Get the fetch error of all employees
export const allEmployeesFetchError = (state: RootState) => state.employees.error;

// Get the update state of an employee
export const updateEmployeeState = (state: RootState) => state.employees.updateStatus;

// Get the update error of an employee
export const updateEmployeeError = (state: RootState) => state.employees.updateError;

// Select an employee by their ID
export const selectEmployeeById = (id: string) => createSelector(
    [selectAllEmployees],
    employees => employees.find(e => e._id === id)
);

// Select employees by their statuses
export const selectEmployeesByStatuses = (statuses: EmploymentStatus[]) => createSelector(
    [selectAllEmployees],
    employees => {
        return statuses.length ? employees.filter(e => statuses.includes(e.status)) : employees;
    }
);

export const hasPendingSalaryChange = (employee: Employee): boolean => {
    return employee.salary_change_requested?.status === EOR_SALARY_CHANGE_STATUS_REQUESTED;
};

// Select and paginate employees matching statuses OR with a pending salary change request
export const selectedEmployeesPaginatedWithSalaryChange = (
    currentPage: number,
    limit: number,
    statuses: string[],
    searchTerm: string | null
) => createSelector(
    [selectAllEmployees],
    employees => {
        employees = employees.filter((e) =>
            statuses.includes(e.status) ||
            hasPendingSalaryChange(e)
        );
        if (searchTerm) {
            employees = employees.filter((e) => {
                const regex = new RegExp(searchTerm, "i");
                return (
                    regex.test(`${e.first_name} ${e.last_name}`) ||
                    regex.test(e.email) ||
                    regex.test(e.description) ||
                    regex.test(e.tax_residence) ||
                    regex.test(e.address) ||
                    regex.test(e.salary.amount.toString())
                )
            })
        }
        return paginateArray(employees, currentPage, limit);
    }
);

// Select and paginate employees based on current page, limit, statuses, and search term
export const selectedEmployeesPaginated = (
    currentPage: number,
    limit: number,
    statuses: string[],
    searchTerm: string | null
) => createSelector(
    [selectAllEmployees],
    employees => {
        if (statuses.length) {
            employees = employees.filter((e) => statuses.includes(e.status))
        }
        if (searchTerm) {
            employees = employees.filter((e) => {
                const regex = new RegExp(searchTerm, "i");
                return (
                    regex.test(`${e.first_name} ${e.last_name}`) ||
                    regex.test(e.email) ||
                    regex.test(e.description) ||
                    regex.test(e.tax_residence) ||
                    regex.test(e.address) ||
                    regex.test(e.salary.amount.toString())
                )
            })
        }
        return paginateArray(employees, currentPage, limit);
    }
);
